import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { layoutDepartment, layoutDepartmentColumns } from '../orgChartLayout';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

// Department accent colors for the org-chart panel.
const DEPT_COLOR = {
  'COLLECTIONS': '#2f8f6b',
  'PUBLIC ENGAGEMENT': '#7a4fa5',
  'ADMINISTRATION': '#3b5ba5',
  'EXHIBITIONS': '#a5533b',
};
const titleCase = (s) => (s ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s);

// Admin/Supervisor: roll-up totals (clickable → jump to that tab) + the live org chart.
// Staff: only their own snapshot.
export default function HROverview({ currentUser, onNavigateTab }) {
  const [data, setData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(API + '/hr/overview', { headers: headers() }).then((r) => r.data).catch(() => null),
      axios.get(API + '/org-chart', { headers: headers() }).then((r) => r.data).catch(() => []),
    ]).then(([ov, pos]) => {
      setData(ov);
      setPositions(Array.isArray(pos) ? pos : []);
    }).finally(() => setLoading(false));
  }, []);

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '18px' };
  const cardStyle = (clickable) => ({ background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: clickable ? 'pointer' : 'default', transition: 'transform .15s, box-shadow .15s' });
  const hoverOn = e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; };
  const hoverOff = e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; };
  const labelStyle = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' };
  const bigStyle = { fontSize: '34px', fontWeight: 700, lineHeight: 1 };
  const subStyle = { fontSize: '12.5px', color: '#6B7280', marginTop: '9px' };
  const unit = (t) => <span style={{ fontSize: '14px', fontWeight: 600, color: '#9CA3AF', marginLeft: '6px' }}>{t}</span>;

  if (loading) return <div style={{ padding: '40px', color: '#9CA3AF', fontSize: '13px' }}>Loading overview…</div>;
  if (!data) return <div style={{ padding: '40px', color: '#9CA3AF', fontSize: '13px' }}>Couldn't load the overview.</div>;

  const isRollup = data.role === 'admin' || data.role === 'supervisor';

  if (isRollup) {
    const cards = [
      { key: 'openings',        label: 'Openings',          value: data.openings.total,   sub: 'open positions',                                                     tab: 'openings',        accent: data.openings.total > 0 },
      { key: 'onboarding',      label: 'Onboarding',        value: data.onboarding.active, sub: 'active cases',                                                       tab: 'onboarding',      accent: data.onboarding.active > 0 },
      { key: 'org-chart',       label: 'Org Chart',         value: data.org_chart.total,  sub: `${data.org_chart.filled} filled · ${data.org_chart.vacant} vacant`,   tab: 'org-chart',       accent: false },
      { key: 'staff-directory', label: 'Staff Directory',   value: data.staff.total,      sub: 'active staff',                                                       tab: 'staff-directory', accent: false },
      { key: 'time-off',        label: 'Time Off / Leave',  value: data.time_off.pending, sub: `${data.time_off.upcoming} upcoming approved`, unit: 'pending',        tab: 'time-off',        accent: data.time_off.pending > 0 },
    ];
    return (
      <div>
        <div style={grid}>
          {cards.map(c => (
            <div key={c.key} style={cardStyle(true)} onClick={() => onNavigateTab && onNavigateTab(c.tab)} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
              <div style={labelStyle}>{c.label}</div>
              <div style={{ ...bigStyle, color: c.accent ? ORANGE : NAVY }}>{c.value}{c.unit && unit(c.unit)}</div>
              <div style={subStyle}>{c.sub}</div>
            </div>
          ))}
        </div>

        <OrgChartPanel positions={positions} onOpen={() => onNavigateTab && onNavigateTab('org-chart')} />
      </div>
    );
  }

  // Staff member — personal snapshot only.
  return (
    <div style={grid}>
      <div style={cardStyle(true)} onClick={() => onNavigateTab && onNavigateTab('time-off')} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
        <div style={labelStyle}>My Time Off</div>
        <div style={{ ...bigStyle, color: data.time_off.pending > 0 ? ORANGE : NAVY }}>{data.time_off.pending}{unit('pending')}</div>
        <div style={subStyle}>{data.time_off.upcoming} upcoming approved</div>
      </div>

      {data.onboarding && (
        <div style={cardStyle(false)}>
          <div style={labelStyle}>My Onboarding</div>
          <div style={{ ...bigStyle, fontSize: '22px', color: NAVY, textTransform: 'capitalize' }}>{data.onboarding.status}</div>
          <div style={subStyle}>{data.onboarding.phase ? `Phase: ${data.onboarding.phase}` : ''}</div>
        </div>
      )}

      <div style={cardStyle(false)}>
        <div style={labelStyle}>My Info</div>
        <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{currentUser?.first_name} {currentUser?.last_name}</div>
        <div style={subStyle}>{data.profile.title || '—'}{data.profile.department ? ` · ${data.profile.department}` : ''}</div>
        <div style={subStyle}>Supervisor: {data.profile.supervisor || '—'}</div>
      </div>
    </div>
  );
}

// ---- Org chart panel: exact live /api/org-chart data, tight clean design. ----
function PersonCard({ p, supervisorName }) {
  const vacant = p.status === 'vacant';
  const temp = p.is_temp;
  const name = p.staff_id ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : 'Vacant';
  const bg = vacant ? '#e9f8ee' : temp ? '#fff3e6' : '#fff';
  const border = vacant ? '#8fd3a6' : temp ? '#f0c48a' : '#d7dee8';
  const sub = p.sub_department ? ` · ${titleCase(p.sub_department)}` : '';
  return (
    <div style={{ width: '100%', maxWidth: 210, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '8px 12px', boxShadow: '0 1px 1px rgba(16,24,40,.05)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, color: vacant ? '#166534' : NAVY }}>{name}</div>
      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, lineHeight: 1.25 }}>{p.position_title}{sub}</div>
      {!vacant && supervisorName && (
        <div style={{ fontSize: 9, color: '#6b7280', marginTop: 3 }}>Supervisor: {supervisorName}</div>
      )}
      {vacant && (p.intended_supervisor_first || p.intended_supervisor_last) && (
        <div style={{ fontSize: 8.5, color: '#166534', fontStyle: 'italic', marginTop: 3 }}>
          Supervised by: {p.intended_supervisor_first || ''} {p.intended_supervisor_last || ''}
        </div>
      )}
    </div>
  );
}

function OrgChartPanel({ positions, onOpen }) {
  const group = (d) => positions.filter(p => p.department === d);
  const executive = group('EXECUTIVE');
  const columns = [
    ['Collections', 'COLLECTIONS'],
    ['Public Engagement', 'PUBLIC ENGAGEMENT'],
    ['Administration', 'ADMINISTRATION'],
    ['Exhibitions', 'EXHIBITIONS'],
  ];
  const asOf = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const legendDot = (bg, br) => ({ width: 11, height: 11, borderRadius: 3, background: bg, border: `1px solid ${br || '#0002'}`, display: 'inline-block' });
  const line = { background: '#c9d2df' };

  return (
    <div style={{ marginTop: 30 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#31415a', margin: 0 }}>Organizational Chart</h2>
          {onOpen && <span onClick={onOpen} style={{ fontSize: 11.5, color: ORANGE, cursor: 'pointer', fontWeight: 600 }}>Open full chart →</span>}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 10.5, color: '#6b7280', alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={legendDot('#fff', '#d7dee8')} />Filled</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={legendDot('#e9f8ee', '#8fd3a6')} />Vacant</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={legendDot('#fff3e6', '#f0c48a')} />Temp-Intermittent</span>
          <span>as of {asOf} · FY 2024/25</span>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e6e9ef', borderRadius: 16, padding: '22px 22px 26px', boxShadow: '0 1px 2px rgba(16,24,40,.04)', overflowX: 'auto' }}>
        {/* EXECUTIVE (top, labeled) */}
        {executive.length > 0 && (() => {
          const sorted = [...executive].sort((a, b) => (a.hierarchy_level - b.hierarchy_level) || (a.display_order - b.display_order));
          const topLvl = sorted[0].hierarchy_level;
          const tops = sorted.filter(p => p.hierarchy_level === topLvl);
          const rest = sorted.filter(p => p.hierarchy_level !== topLvl);
          const primary = rest[0] || null;   // e.g. Deputy Director (Jon) — stays centered
          const extras = rest.slice(1);       // peers / vacancies — placed to Jon's right
          const execCard = (p) => {
            const vac = p.status === 'vacant', tmp = p.is_temp, npy = p.is_not_in_py_count;
            const bg = tmp ? '#fff3e6' : npy ? '#f3f4f6' : vac ? '#d4f3df' : '#fff';
            const brd = tmp ? '#f0c48a' : npy ? '#d1d5db' : vac ? '#5cb97f' : ORANGE;
            return (
              <div style={{ width: 240, flex: '0 0 auto' }}>
                <div style={{ background: bg, border: `1.5px solid ${brd}`, borderRadius: 10, padding: '8px 12px', boxShadow: '0 1px 1px rgba(16,24,40,.05)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2, color: vac ? '#166534' : NAVY }}>{p.staff_id ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : 'Vacant'}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{p.position_title}</div>
                  {(p.supervisor_first || p.supervisor_last) && (
                    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 3 }}>Supervisor: {`${p.supervisor_first || ''} ${p.supervisor_last || ''}`.trim()}</div>
                  )}
                </div>
              </div>
            );
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '5px 16px', borderRadius: 8, marginBottom: 12, background: ORANGE }}>Executive</div>
              {tops.map((p, i) => (
                <React.Fragment key={p.id}>
                  {execCard(p)}
                  {i < tops.length - 1 && <div style={{ ...line, width: 2, height: 16 }} />}
                </React.Fragment>
              ))}
              {primary && (
                <>
                  <div style={{ ...line, width: 2, height: 16 }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'start', columnGap: 16, width: '100%' }}>
                    <div />
                    <div style={{ display: 'flex', justifyContent: 'center' }}>{execCard(primary)}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                      {extras.map(p => <React.Fragment key={p.id}>{execCard(p)}</React.Fragment>)}
                    </div>
                  </div>
                </>
              )}
              <div style={{ ...line, width: 2, height: 16 }} />
              <div style={{ ...line, height: 2, width: '78%' }} />
              <div style={{ display: 'flex', justifyContent: 'space-around', width: '78%' }}>
                {columns.map((_, i) => <div key={i} style={{ ...line, width: 2, height: 14 }} />)}
              </div>
            </div>
          );
        })()}

        {/* Department columns — sub-department heads (leads) sit side-by-side, reports below */}
        <div style={{ display: 'flex', gap: 28, marginTop: 2, alignItems: 'flex-start', width: 'max-content' }}>
          {columns.map(([label, key]) => {
            const cols = layoutDepartmentColumns(group(key));
            return (
              <div key={key} style={{ flex: '0 0 auto' }}>
                <div style={{ color: '#fff', textAlign: 'center', fontWeight: 700, fontSize: 10.5, letterSpacing: '0.05em', padding: 7, borderRadius: 8, marginBottom: 14, textTransform: 'uppercase', background: DEPT_COLOR[key] }}>{label}</div>
                <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'flex-start' }}>
                  {cols.map((col, i) => (
                    <div key={col.lead ? col.lead.pos.id : 'x' + i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {col.lead && <div style={{ width: 210, flex: '0 0 auto' }}><PersonCard p={col.lead.pos} supervisorName={col.lead.supervisorName} /></div>}
                      {col.lead && col.reports.length > 0 && <div style={{ width: 2, height: 16, background: '#c9d2df' }} />}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                        {col.reports.map(({ pos, supervisorName }) => (
                          <div key={pos.id} style={{ width: 210, flex: '0 0 auto' }}><PersonCard p={pos} supervisorName={supervisorName} /></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
